import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createElementVNode as _createElementVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createVNode as _createVNode, Teleport as _Teleport } from "vue"

const _hoisted_1 = { class: "doc-more-menu" }
const _hoisted_2 = { class: "doc-more-menu__list" }
const _hoisted_3 = ["disabled"]
const _hoisted_4 = ["disabled"]
const _hoisted_5 = ["href"]

import { nextTick, onMounted, ref, watch } from 'vue'
import type { Raw } from 'vue'

import { useWait } from '@/app/composables/useWait'
import {
  canDownload,
  canMarkFinished,
  canMarkNotStarted,
  docRouteNames,
  DocView,
} from '@/models/doc'
import type { DocsView } from '@/models/doc'
import { DocCustomListsView } from '@/models/doc.custom.lists'
import {
  markDocFinishedWithUndo,
  markDocNotStartedWithUndo,
} from '@/models/doc.read.state'
import type { UserDocListItem } from '@/models/interfaces'
import { platform } from '@/services/platform'
import { Sentry } from '@/services/sentry'

import BasePopup from '@/components/ui/BasePopup.vue'
import AddToListPopup from '@/app/components/doc/AddToListPopup.vue'

/**
 * The doc's "more" menu with the Save destination menu and reading state
 * actions. It replaces `DocLibraryMenu` and the lists menus.
 *
 * Like `DocLibraryMenu`, it can be used in a doc listing page by passing the
 * `docsView` prop, or in a single doc page, by passing a `DocView` instance
 * to the `doc` prop.
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'DocMoreMenu',
  props: {
    doc: {},
    docsView: {},
    hideDownload: { type: Boolean }
  },
  setup(__props: any) {

const props = __props

const isSingleDocPage = props.doc instanceof DocView
if (!isSingleDocPage && !props.docsView) {
  throw new Error(
    'Incorrect usage of DocMoreMenu. ' +
      'If `doc` is not an instance of `DocView`, ' +
      'then `docsView` must not be undefined.',
  )
}

const menuButton = ref<HTMLElement | null>(null)
const menu = ref<InstanceType<typeof BasePopup> | null>(null)
const destinationMenu = ref<InstanceType<typeof AddToListPopup> | null>(null)
const wait = useWait()
const listsView = ref<Raw<DocCustomListsView>>(
  new DocCustomListsView(props.doc, wait, props.docsView),
)
// The reading page replaces the DocView when a different guide is opened.
// This component stays mounted, so update here with the listsView too.
watch(
  () => props.doc,
  (doc) => {
    listsView.value = new DocCustomListsView(doc, wait, props.docsView)
  },
)
const isBusy = ref(false)

// Mount the popup when the menu is first opened.
const hasOpened = ref(false)

// We use this to hide the Amazon link on Android (see `DocLibraryMenu`). The
// link is only shown on a single doc page.
const isAndroidDevice = ref(false)
onMounted(async () => {
  if (isSingleDocPage) {
    isAndroidDevice.value = await platform.isNativeAndroidApp()
  }
})

async function openMenu(): Promise<void> {
  hasOpened.value = true
  // Wait for the popup to mount before showing it.
  await nextTick()
  menu.value?.show()
}

async function openDestinationMenu(): Promise<void> {
  menu.value?.hide()
  await destinationMenu.value?.show()
}

/**
 * Close the menu and run `action` on the doc, ignoring repeated clicks while
 * it's pending.
 */
async function runAction(
  action: (
    doc: UserDocListItem | DocView,
    docsView?: DocsView,
  ) => Promise<void>,
): Promise<void> {
  menu.value?.hide()
  isBusy.value = true
  try {
    await action(props.doc, props.docsView)
  } catch (error) {
    Sentry.captureException(error)
  } finally {
    isBusy.value = false
  }
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      ref_key: "menuButton",
      ref: menuButton,
      class: "doc-more-menu__button-wrapper",
      onClick: _withModifiers(openMenu, ["stop","prevent"])
    }, [
      _renderSlot(_ctx.$slots, "default", {}, () => [
        _cache[4] || (_cache[4] = _createElementVNode("button", {
          class: "doc-more-menu__button",
          "aria-label": "More actions"
        }, [
          _createElementVNode("span", {
            class: "doc-more-menu__button-icon iconfont imore",
            "aria-hidden": "true"
          })
        ], -1))
      ])
    ], 512),
    (hasOpened.value)
      ? (_openBlock(), _createBlock(_Teleport, {
          key: 0,
          to: "#appRoot"
        }, [
          _createVNode(BasePopup, {
            ref_key: "menu",
            ref: menu,
            class: "doc-more-menu__popup",
            menu: "",
            anchor: menuButton.value
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("button", {
                  class: "doc-more-menu__item",
                  onClick: openDestinationMenu
                }, [
                  _createElementVNode("span", {
                    class: _normalizeClass(["doc-more-menu__icon iconfont", listsView.value.isDocInAnyList ? 'iflag2' : 'iflag']),
                    "aria-hidden": "true"
                  }, null, 2),
                  _createTextVNode(" " + _toDisplayString(listsView.value.isDocInAnyList ? 'Saved' : 'Save'), 1)
                ]),
                (_unref(canMarkFinished)(__props.doc))
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      class: "doc-more-menu__item",
                      disabled: isBusy.value,
                      onClick: _cache[0] || (_cache[0] = ($event: any) => (runAction(_unref(markDocFinishedWithUndo))))
                    }, [...(_cache[5] || (_cache[5] = [
                      _createElementVNode("span", {
                        class: "doc-more-menu__icon iconfont icheck-bold",
                        "aria-hidden": "true"
                      }, null, -1),
                      _createTextVNode(" Mark as Finished ", -1)
                    ]))], 8, _hoisted_3))
                  : _createCommentVNode("", true),
                (_unref(canDownload)(__props.doc) && !__props.hideDownload)
                  ? (_openBlock(), _createBlock(_component_router_link, {
                      key: 1,
                      class: "doc-more-menu__item",
                      to: {
              name: _unref(docRouteNames)[__props.doc.doc_type].export,
              params: { url_slug: __props.doc.url_slug },
            },
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (menu.value?.hide()))
                    }, {
                      default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                        _createElementVNode("span", {
                          class: "doc-more-menu__icon iconfont idownload",
                          "aria-hidden": "true"
                        }, null, -1),
                        _createTextVNode(" Download ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["to"]))
                  : _createCommentVNode("", true),
                (_unref(canMarkNotStarted)(__props.doc))
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 2,
                      class: "doc-more-menu__item",
                      disabled: isBusy.value,
                      onClick: _cache[2] || (_cache[2] = ($event: any) => (runAction(_unref(markDocNotStartedWithUndo))))
                    }, [...(_cache[7] || (_cache[7] = [
                      _createElementVNode("span", {
                        class: "doc-more-menu__icon iconfont ireset",
                        "aria-hidden": "true"
                      }, null, -1),
                      _createTextVNode(" Mark as Not Started ", -1)
                    ]))], 8, _hoisted_4))
                  : _createCommentVNode("", true),
                (isSingleDocPage && __props.doc.amazon_link && !isAndroidDevice.value)
                  ? (_openBlock(), _createElementBlock("a", {
                      key: 3,
                      class: "doc-more-menu__item",
                      href: __props.doc.amazon_link,
                      target: "_blank",
                      onClick: _cache[3] || (_cache[3] = ($event: any) => (menu.value?.hide()))
                    }, [...(_cache[8] || (_cache[8] = [
                      _createElementVNode("span", {
                        class: "doc-more-menu__icon iconfont icart-buy",
                        "aria-hidden": "true"
                      }, null, -1),
                      _createTextVNode(" Buy on Amazon ", -1)
                    ]))], 8, _hoisted_5))
                  : _createCommentVNode("", true)
              ])
            ]),
            _: 1
          }, 8, ["anchor"]),
          _createVNode(AddToListPopup, {
            ref_key: "destinationMenu",
            ref: destinationMenu,
            view: listsView.value,
            newStyle20321B: true,
            anchor: menuButton.value
          }, null, 8, ["view", "anchor"])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})